/**
 * Lightweight client-side session encryption using Web Crypto API.
 *
 * This is intentionally a simple, local-only encryption layer to avoid
 * storing credentials as plain JSON in localStorage. The encryption key is
 * stored locally as well (in base64) which means this protects against
 * casual inspection but is not a substitute for server-side secure storage.
 */

// Simple synchronous XOR-based obfuscation.
// NOTE: This is not cryptographically secure. It prevents casual inspection of
// the stored session but should not be treated as a substitute for proper
// server-side secrets management. It has the advantage of being synchronous
// so it can be used by existing synchronous APIs.

const KEY_STORAGE_KEY = 'orbitflow_crypto_key_v1'

function getOrCreateKeySync(): Uint8Array {
    if (typeof window === 'undefined') return new Uint8Array([0])

    const existing = localStorage.getItem(KEY_STORAGE_KEY)
    if (existing) {
        return Uint8Array.from(atob(existing), c => c.charCodeAt(0))
    }

    // Create 32-byte (256-bit) random key
    const key = new Uint8Array(32)
    if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(key)
    } else {
        for (let i = 0; i < key.length; i++) key[i] = Math.floor(Math.random() * 256)
    }
    localStorage.setItem(KEY_STORAGE_KEY, btoa(String.fromCharCode(...Array.from(key))))
    return key
}

function xorEncode(data: Uint8Array, key: Uint8Array): Uint8Array {
    const out = new Uint8Array(data.length)
    for (let i = 0; i < data.length; i++) {
        out[i] = data[i] ^ key[i % key.length]
    }
    return out
}

function bufferToBase64(buf: Uint8Array) {
    let binary = ''
    for (let i = 0; i < buf.length; i++) binary += String.fromCharCode(buf[i])
    return btoa(binary)
}

function base64ToBuffer(b64: string) {
    const bin = atob(b64)
    const arr = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
    return arr
}

export function encryptSessionObjectSync(obj: any): string | null {
    try {
        const key = getOrCreateKeySync()
        const encoder = new TextEncoder()
        const data = encoder.encode(JSON.stringify(obj))
        const enc = xorEncode(data, key)
        return bufferToBase64(enc)
    } catch (err) {
        console.error('[session-crypt] encrypt failed', err)
        return null
    }
}

export function decryptSessionStringSync(encryptedStr: string): any | null {
    try {
        const key = getOrCreateKeySync()
        const data = base64ToBuffer(encryptedStr)
        const dec = xorEncode(data, key)
        const decoder = new TextDecoder()
        return JSON.parse(decoder.decode(dec))
    } catch (err) {
        console.error('[session-crypt] decrypt failed', err)
        return null
    }
}

